import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {v4 as uuid} from 'uuid'
import {useEffect, useMemo, useState} from 'react'
import {Checkbox, Grid2} from '@mui/material'
import {BeatLoader} from 'react-spinners'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Trans, useTranslation} from 'react-i18next'
import Section from '../../Dashboard/Section'
import AivatarClient from '../../../services/AivatarClient'
import env from '../../../constants/env'
import PriceFormat from '../../Text/PriceFormater'
import {workspaceQueries} from '../../../services/workspace'
import {downgradeSubscribe, paymentsQueries, upgradeSubscribe} from '../../../services/payments'
import {getPlanColor} from '../../../utils/colors'
import useDialog from '../../../hooks/useDialog'
import PlanFeatures from '../PlanFeatures'
import AivatarButton from '../../buttons/AivatarButton'
import Receipt from './Receipt'
import useChangePaymentMethod from '../../../hooks/useChangePaymentMethod'
import {
  useCurrentWorkspaceCreditsQuery,
  useCurrentWorkspaceDetailQuery,
  useCurrentWorkspacePaymentMethod,
} from '../../../hooks/queries'
import {getCardInfo, numberSubjectParticle, numberTopicParticle} from '../../../utils/number'
import {customDayjs} from '../../../utils/date'
import useURI from '../../../hooks/useURI'
import {trackEvent} from "../../../analytics/eventTracker";
import {
  ANNUAL,
  MONTHLY,
  assertAnnualAmountMatchesServer,
  billingMonths,
  getAnnualDiscountPercent,
  isAnnual,
  unelapsedMonths,
} from '../../../services/payments/annual'
import {getMonthlyCredit, planQueries} from '../../../services/plan'
import AnnualNotice from './AnnualNotice'

export default function Checkout() {
  const queryClient = useQueryClient()
  const dialog = useDialog()

  const [searchParams] = useSearchParams()
  const {workspaceId} = useParams()
  const planId = searchParams.get('plan')
  // 주기는 1·12 만 받는다. 종전에는 12 만 읽어 월간이 undefined 로 떨어졌고,
  // BE 가 그것을 "현재 주기 유지"로 읽어 연간 구독자의 연 → 월 전환이 성립하지 않았다.
  // 값이 없는 진입점(주기 선택 UI 가 없는 PlanItem)은 그대로 undefined 를 보낸다.
  const intervalParam = Number(searchParams.get('interval'))
  const interval =
    // eslint-disable-next-line no-nested-ternary
    intervalParam === ANNUAL ? ANNUAL : intervalParam === MONTHLY ? MONTHLY : undefined
  const {data: paymentMethod} = useCurrentWorkspacePaymentMethod()
  const {data: workspace} = useCurrentWorkspaceDetailQuery()
  const {data: credits} = useCurrentWorkspaceCreditsQuery()
  const {data: plans} = useQuery(planQueries.list)
  const {
    data: checkout,
    isError: isCheckoutError,
    error: checkoutError,
  } = useQuery({...paymentsQueries.checkout({workspaceId, planId, interval}), retry: false})
  const {mutate: mutateToUpgradeSubscribe, isLoading: isUpgrading} = useMutation({
    mutationFn: upgradeSubscribe,
  })
  const {mutate: mutateToDowngradeSubscribe, isLoading: isDowngrading} = useMutation({
    mutationFn: downgradeSubscribe,
  })
  const {changePaymentMethod, isChangingPaymentMethod} = useChangePaymentMethod()

  // const [plan, setPlan] = useState({});
  const [proceeding, setProceeding] = useState(false)
  const [termsChecked, setTermsChecked] = useState(false)

  const noticeUrl = `${env.API_HOST}/webhooks/payment`
  const navigate = useNavigate()
  const {t, i18n} = useTranslation('plan-checkout')

  const paidOnce = async (body) => {
    const {data} = await AivatarClient().post('/payments/paid_once', body)
    return data
  }

  const cb = async (resp) => {
    if (resp.success) {
      try {
        const body = {
          workspaceId,
          planId: checkout?.next.plan.id,
          interval,
          result: {
            pg: 'portone',
            ...resp,
          },
        }

        // todo: 결제 중 로더
        setProceeding(true)
        await paidOnce(body)
        await queryClient.invalidateQueries({
          queryKey: workspaceQueries.detail(workspaceId).queryKey,
        })
        navigate(`/workspace/${workspaceId}/subscription/checkout/complete`, {
          replace: true,
        })
      } catch (e) {
        console.error(e)
        // eslint-disable-next-line no-alert
        window.alert(`결제 결과 전송 실패 - 관리자에게 문의바랍니다.: ${JSON.stringify(e)}`)
      } finally {
        setProceeding(false)
      }
    } else {
      console.log(resp)
      if (resp.error_msg.includes('PAY_PROCESS_CANCELED')) {
        // eslint-disable-next-line no-alert
        return window.alert('사용자가 결제를 취소하였습니다.')
      }
      // eslint-disable-next-line no-alert
      return window.alert(`결제 실패: ${resp.error_msg}`)
    }

    return undefined
  }

  // 즉시/예약 판정은 서버가 한다 (BE SubscriptionChangeTiming). 등급 비교로는
  // "연 PRO → 월 PRO" 같은 동등급 전환을 가릴 수 없어 결제창으로 새어 나간다.
  // next.status 가 CHANGED 면 만료일 적용 예약, ACTIVE 면 즉시 전환이다.
  const isScheduledChange = checkout?.next?.status === 'CHANGED'
  const isImmediateChange = Boolean(checkout) && !isScheduledChange
  const hasPaymentMethod = Boolean(paymentMethod?.paymentMethodId)

  const nextIsAnnual = isAnnual(checkout?.next?.intervalMonth)
  const currentIsAnnual = isAnnual(checkout?.current?.intervalMonth)

  // 미도래 개월 수. 차감액은 next.price − chargeAmount 로 떨어지지만 개월 수는
  // 응답에 없어 BE SubscriptionPeriod.unelapsedMonths 를 재현한다.
  const unelapsed = unelapsedMonths(checkout?.current)

  const nextPlan = plans?.find((plan) => plan.id === checkout?.next?.plan?.id)
  const monthlyCredit = getMonthlyCredit(nextPlan) ?? getMonthlyCredit(checkout?.next?.plan)
  const remainingCredit = credits?.subscriptionCredit ?? null
  // 구독 외 잔액(크레딧 팩 · 쿠폰 · 어드민 부여 = BE singleCredit)은 전환과 무관하게 그대로 남는다.
  // 빼고 합계를 내면 결제 뒤 실제 잔액보다 작게 안내된다(Phase 2 QA 55 — 안내 1,800 · 실제 3,900)
  const otherCredit = Number(credits?.singleCredit ?? 0)
  // 비활성 워크스페이스의 재구독이다. 다른 고객 화면과 같이 현재 구독이 PAUSE 면 비활성으로 본다.
  // 남은 구독 크레딧이 0이고 직전 약정이 끝났으므로 전환 문장 · 기존 플랜 줄이 맞지 않는다(Phase 2 QA 55 · E3)
  const isResubscribe = workspace?.subscription?.status === 'PAUSE'
  const annualDiscountPercent = getAnnualDiscountPercent(nextPlan)

  // 표시가와 서버 정가가 갈라지면 FE 할인율 상수가 서버 설정과 벌어진 것이다.
  // 실제 청구는 서버 chargeAmount 가 하므로 결제 금액이 틀리지는 않지만,
  // 플랜 모달이 잘못된 가격을 보여주고 있다는 뜻이라 즉시 드러나야 한다.
  useEffect(() => {
    if (nextIsAnnual && nextPlan) {
      assertAnnualAmountMatchesServer(nextPlan, Number(checkout?.next?.price))
    }
  }, [nextIsAnnual, nextPlan, checkout])

  const scheduled = workspace?.nextSubscription?.status === 'CHANGED' ? workspace.nextSubscription : null

  // 등급과 주기를 함께 예약한 건(결합 예약) 위에 또 예약을 걸면 두 예약이 공존한다.
  // 그때의 전이 규칙이 아직 확정 전이라 이 경로만 UI 에서 임시로 막는다 (BE T-D3 게이트).
  const blockedByExistingSchedule = Boolean(
    scheduled &&
      isScheduledChange &&
      scheduled.plan?.id !== workspace?.subscription?.plan?.id &&
      billingMonths(scheduled.intervalMonth) !== billingMonths(workspace?.subscription?.intervalMonth),
  )

  // 예약 중 업그레이드는 막지 않는다. 예약이 자동 철회되고 즉시 전환이 적용된다
  // (정책 11.8, BE updateSubscriptionDto 가 기존 next 를 INACTIVE 로 밀어낸다).
  const scheduleWillBeReplaced = Boolean(scheduled) && isImmediateChange

  const onClickPayment = () => {
    // 즉시 전환인데 등록된 결제수단이 없으면 아래 결제창(paid_once)으로 내려간다.
    if (isImmediateChange && hasPaymentMethod) {
      trackEvent('payment_button_clicked', {
        workspace_id: workspaceId,
      });

      dialog.confirm({
        title: nextIsAnnual && !currentIsAnnual ? t('연간 결제로 전환하시겠습니까?') : t('플랜을 업그레이드하시겠습니까?'),
        message: nextIsAnnual
          ? t('확인을 누르면 결제가 진행되고 오늘부터 새 1년이 시작됩니다.')
          : t('확인을 누르면 결제가 진행되고 플랜이 업그레이드됩니다.'),
        onClickConfirm: () => {
          setProceeding(true)
          mutateToUpgradeSubscribe(
            {
              workspaceId,
              paymentMethodId: paymentMethod?.paymentMethodId,
              planId,
              interval,
            },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({
                  queryKey: workspaceQueries.detail(workspaceId).queryKey,
                })
                dialog.close()
                navigate(`/workspace/${workspaceId}/subscription/checkout/complete`, {replace: true})
              },
              onError: () => {
                dialog.alert({
                  message: '플랜 업그레이드에 실패했습니다. 관리자에게 문의바랍니다.',
                })
              },
              onSettled: () => {
                setProceeding(false)
              },
            },
          )
        },
      })

      return
    }

    if (isScheduledChange) {
      dialog.confirm({
        title: t('변경을 예약하시겠습니까?'),
        message: t('확인을 누르면 현재 약정 만료일부터 변경이 적용됩니다. 중간 정산이나 환불은 없습니다.'),
        onClickConfirm: () => {
          trackEvent('plan_downgrade_requested', {
            previous_plan: checkout?.current?.plan?.name,
            new_plan: checkout?.next?.plan?.name,
            workspace_id: workspaceId,
            change_effective_date: checkout?.next?.startedAt,
          });

          setProceeding(true)
          mutateToDowngradeSubscribe(
            {
              workspaceId,
              paymentMethodId: paymentMethod?.paymentMethodId,
              planId,
              interval,
            },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({
                  queryKey: workspaceQueries.detail(workspaceId).queryKey,
                })
                dialog.close()
                navigate(`/workspace/${workspaceId}/subscription/checkout/complete`, {replace: true})
              },
              onError: () => {
                dialog.alert({
                  message: '변경 예약에 실패했습니다. 관리자에게 문의바랍니다.',
                })
              },
              onSettled: () => {
                setProceeding(false)
              },
            },
          )
        },
      })

      return
    }

    const {IMP} = window
    IMP.init(env.IMP)
    const cuid = uuid()
    const muid = uuid()

    const data = {
      pg: env.PG,
      pay_method: 'card',
      merchant_uid: muid,
      customer_uid: cuid,
      // 표시가와 청구액이 갈리지 않게 서버가 산정한 이번 회 청구액을 쓴다.
      // 연간 즉시 전환에서는 next.price(정가)와 다른 값이다 (BE aivatar PR #443).
      amount: checkout?.chargeAmount,
      name: env.PAY_NAME,
      popup: true,
      language: i18n.language,
      notice_url: noticeUrl,
    }

    IMP.request_pay(data, cb)
  }

  // function onClickTest() {
  //   let r = {
  //     success: true,
  //     imp_uid: env.IMP,
  //     pay_method: 'card',
  //     merchant_uid: 'order-1099999', // 주문번호
  //     name: CONSTANTS.PAYMENT.NAME,
  //     paid_amount: priceObj.amount,
  //     currency: 'KRW',
  //     pg_provider: CONSTANTS.PAYMENT.PG,
  //     pg_type: 'payment',
  //     pg_tid: '',
  //     apply_num: '',
  //     buyer_name: '',
  //     buyer_email: '',
  //     buyer_tel: '',
  //     buyer_addr: '',
  //     buyer_postcode: '',
  //     custom_data: null,
  //     status: 'paid',
  //     paid_at: 1699400690,
  //     receipt_url: '',
  //     card_name: '신한카드',
  //     bank_name: null,
  //     card_quota: 0,
  //     card_number: '54287966****705*',
  //     customer_uid: 'cuid-102938',
  //   }
  //   cb(r).then(console.log)
  // }

  const nextPaymentDate = useMemo(() => {
    // 예약 전환은 현재 약정 만료일 다음 날부터 새 플랜이 시작된다.
    if (isScheduledChange) {
      return customDayjs(checkout?.current.endAt).add(1, 'day').format('YYYY-MM-DD')
    }

    return customDayjs(checkout?.next.endAt).add(1, 'day').format('YYYY-MM-DD')
  }, [checkout, isScheduledChange])

  const {uri} = useURI()
  return (
    <Section className="mt-[40px]" title={t('결제 내역')}>
      {isChangingPaymentMethod ||
        (proceeding && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              position: 'fixed',
              zIndex: 9999,
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            <BeatLoader color="#3498db" loading={proceeding} size={24}/>
            <p style={{color: '#fff', marginTop: '20px'}}>Requesting...</p>
          </div>
        ))}

      <div className="mt-[35px]">
        <Grid2 container spacing="45px">
          <Grid2 item size={6}>
            <div className="bg-gray-6 rounded-[5px] p-[5px]">
              <table className="[&_td:first-child]:text-gray-3 w-full text-[17px] [&_td]:p-[15px] [&_td]:font-[700]">
                <colgroup>
                  <col className="w-[150px]"/>
                  <col/>
                  <col className="w-[150px]"/>
                </colgroup>
                {/* 비활성 재구독은 끝난 플랜이 아직 구독 중인 것처럼 읽히지 않게 기존 플랜 줄을 숨긴다 */}
                {isResubscribe ? null : (
                  <tr>
                    <td>{t('기존 플랜')}</td>
                    <td>
                      <span
                        translate="no"
                        className="inline-block text-[var(--color)]"
                        style={{
                          '--color': getPlanColor(checkout?.current.plan.displayName),
                        }}
                      >
                        {checkout?.current.plan.displayName}
                      </span>
                      <span className="text-gray-21 ml-[6px] text-[13px] font-[400]">
                        {currentIsAnnual ? t('연간') : t('월간')}
                      </span>
                    </td>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <td>
                      <PriceFormat amount={checkout?.current.price}/>
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{t('신규 플랜')}</td>
                  <td>
                    <span
                      translate="no"
                      className="inline-block text-[var(--color)]"
                      style={{
                        '--color': getPlanColor(checkout?.next.plan.displayName),
                      }}
                    >
                      {checkout?.next.plan.displayName}
                    </span>
                    <span className="text-gray-21 ml-[6px] text-[13px] font-[400]">
                      {nextIsAnnual ? t('연간') : t('월간')}
                    </span>
                    <br/>
                  </td>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <td>
                    <PriceFormat amount={checkout?.next.price}/>
                  </td>
                </tr>
              </table>
            </div>

            <div className="mt-[25px]">
              <div className="mb-[15px] flex items-end justify-between">
                <div className="pl-[20px] text-[17px] font-[700]">{t('결제 수단')}</div>
                {/* {paymentMethod ? (
                  <AivatarButton type="button" variant="m4" onClick={() => changePaymentMethod()}>
                    {t('변경')}
                  </AivatarButton>
                ) : null} */}
              </div>
              <div className="bg-gray-6 rounded-[5px] px-[30px] py-[25px] leading-[1.93]">
                {paymentMethod ? (
                  <div
                    key={`${paymentMethod.cardName}-${paymentMethod.cardNumber}`}
                    className="text-[16px] font-[700] text-[#5e5e5e]"
                  >
                    {paymentMethod ? getCardInfo(paymentMethod.cardName, paymentMethod.cardNumber) : null}
                  </div>
                ) : (
                  <span className="text-gray-3 font-[700]">{t('아직 등록된 결제 수단이 없습니다.')}</span>
                )}
              </div>
            </div>

            <div className="mt-[35px]">
              <div className="mb-[15px] pl-[20px] text-[17px] font-[700]">
                {t('{{plan_name}} 플랜에는 아래 사항이 포함됩니다.', {
                  plan_name: checkout?.next.plan.displayName,
                })}
              </div>
              <div className="bg-gray-6 rounded-[5px] px-[30px] py-[25px] leading-[1.93]">
                {checkout?.next.plan.name ? <PlanFeatures planName={checkout?.next.plan.name}/> : null}
              </div>
            </div>
          </Grid2>
          <Grid2 item size={6}>
            {checkout ? (
              <Receipt
                className="mt-[30px]"
                plan={checkout?.next.plan}
                intervalMonth={checkout?.next.intervalMonth}
                price={Number(checkout.next.price)}
                chargeAmount={Number(checkout.chargeAmount)}
                deductionMonths={unelapsed}
              />
            ) : null}
            <span className="inline-block pl-[7px] text-[12px] font-[400]">
              {/* 즉시 전환·신규 결제는 오늘 청구된다. 다음 결제일은 절대 날짜로 적는다 (정책 6.2) */}
              {isImmediateChange ? (
                <span>
                  {t('{{paid_at}} 결제 · 다음 결제일 {{next_at}}', {
                    paid_at: customDayjs().format('YYYY-MM-DD'),
                    next_at: nextPaymentDate,
                  })}
                </span>
              ) : (
                <>
                  <span>{t('다음 결제일')}: </span>
                  <span>{nextPaymentDate}</span>
                </>
              )}
            </span>
            {isScheduledChange ? (
              <p className="text-point-3 pl-[7px] text-[14px]">지금 결제되지 않고, 다음 결제일에 결제됩니다.</p>
            ) : null}

            {/* 즉시 전환은 결제일·크레딧 지급일을 오늘로 재설정한다. 기존 만료일이
                사라지는 점이 오해를 부르기 쉬워 명시한다 (정책 11.5·11.7) */}
            {isImmediateChange && nextIsAnnual ? (
              <p className="text-gray-21 mt-[8px] pl-[7px] text-[13px]">
                {t('오늘부터 새 1년이 시작됩니다. 다음 결제일은 {{date}}입니다.', {date: nextPaymentDate})}
              </p>
            ) : null}

            {/* 비활성 재구독 — 첫 달 지급분과, 구독 외 크레딧이 있으면 결제 직후 잔액(#297 과 같은 계산) */}
            {isImmediateChange && isResubscribe && monthlyCredit != null ? (
              <p className="text-gray-21 mt-[8px] pl-[7px] text-[13px] leading-[1.6]">
                {t(
                  otherCredit > 0
                    ? '{{plan_name}} 첫 달 크레딧 {{granted}}{{subject}} 지급됩니다. 결제 직후 잔액은 {{total}}입니다(크레딧 팩 등 {{pack}} 포함).'
                    : '{{plan_name}} 첫 달 크레딧 {{granted}}{{subject}} 지급됩니다.',
                  {
                    plan_name: checkout?.next.plan.displayName,
                    granted: Number(monthlyCredit).toLocaleString(),
                    // 숫자 끝소리에 맞춘 조사 (1,800이 · 1,732가)
                    subject: numberSubjectParticle(Number(monthlyCredit)),
                    total: (Number(monthlyCredit) + otherCredit).toLocaleString(),
                    pack: otherCredit.toLocaleString(),
                  },
                )}
              </p>
            ) : null}

            {/* 크레딧 합산 안내. "소멸" 이라는 말을 쓰지 않는다 (정책 11.5, D-35) */}
            {isImmediateChange && !isResubscribe && monthlyCredit != null && remainingCredit != null ? (
              <p className="text-gray-21 mt-[8px] pl-[7px] text-[13px] leading-[1.6]">
                {t(
                  otherCredit > 0
                    ? '지금 남은 {{remaining}}{{topic}} 사라지지 않고 {{plan_name}} 첫 달 지급분 {{granted}}에 더해집니다. 전환 직후 잔액은 {{total}}입니다(크레딧 팩 등 {{pack}} 포함).'
                    : '지금 남은 {{remaining}}{{topic}} 사라지지 않고 {{plan_name}} 첫 달 지급분 {{granted}}에 더해집니다. 전환 직후 잔액은 {{total}}입니다.',
                  {
                    remaining: Number(remainingCredit).toLocaleString(),
                    // 숫자 끝소리에 맞춘 조사 (1,800은 · 1,732는). 영어 문장은 쓰지 않는다
                    topic: numberTopicParticle(Number(remainingCredit)),
                    plan_name: checkout?.next.plan.displayName,
                    granted: Number(monthlyCredit).toLocaleString(),
                    total: (Number(remainingCredit) + Number(monthlyCredit) + otherCredit).toLocaleString(),
                    pack: otherCredit.toLocaleString(),
                  },
                )}
              </p>
            ) : null}

            {/* 크레딧 지급 주기 안내 — 결제 1회와 지급 12회가 갈린다는 사실을 결제 전에 알린다 */}
            {nextIsAnnual && monthlyCredit != null ? (
              <p className="text-gray-21 mt-[8px] pl-[7px] text-[13px] leading-[1.6]">
                {t('크레딧은 매월 결제일과 같은 날짜에 {{credit}}씩 지급됩니다. 결제는 연 1회입니다.', {
                  credit: Number(monthlyCredit).toLocaleString(),
                })}
              </p>
            ) : null}

            {/* 확정 문안 ① 할인 소급 고지 — 연간을 새로 사는 화면 */}
            {nextIsAnnual ? (
              <AnnualNotice>
                {t(
                  '연간 플랜은 1년 약정을 조건으로 월간 요금 대비 {{percent}}% 할인이 적용되는 별도 요금 상품입니다. 약정 기간 중 구독을 취소하면 이용한 기간은 할인 전 월 정가로 계산해 차감하고 남은 금액을 환불합니다.',
                  {percent: annualDiscountPercent},
                )}
              </AnnualNotice>
            ) : null}

            {/* 확정 문안 ② 청약철회 고지 — 즉시 전환 3종 공통 */}
            {isImmediateChange && (nextIsAnnual || currentIsAnnual) ? (
              <AnnualNotice>
                {t(
                  '전환 결제 후 7일 이내에 취소하면 이전 플랜으로 되돌리고 결제 금액을 환불합니다. 전환 후 사용한 만큼은 환불액에서 차감되며, 환불은 전환 결제 건과 이전 결제 건으로 나뉘어 카드 명세서에 표시될 수 있습니다.',
                )}
              </AnnualNotice>
            ) : null}

            {isCheckoutError ? (
              <p className="text-point-3 mt-[14px] whitespace-pre-wrap break-keep rounded-[5px] border border-[#d5b8b8] bg-[#f4eeee] px-[16px] py-[13px] text-[13px] leading-[1.6]">
                {checkoutError?.response?.data?.message ??
                  t('결제 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')}
              </p>
            ) : null}

            {scheduleWillBeReplaced ? (
              <p className="text-point-3 mt-[14px] break-keep text-[13px] leading-[1.6]">
                {t('예약된 변경이 취소되고 지금 바로 {{plan_name}}으로 바뀝니다.', {
                  plan_name: checkout?.next.plan.displayName,
                })}
              </p>
            ) : null}

            {blockedByExistingSchedule ? (
              <p className="text-point-3 mt-[14px] break-keep rounded-[5px] border border-[#d5b8b8] bg-[#f4eeee] px-[16px] py-[13px] text-[13px] leading-[1.6]">
                {t('이미 예약된 변경이 있어 지금은 신청할 수 없습니다. 예약을 취소한 뒤 다시 시도해 주세요.')}
              </p>
            ) : null}
            <div className="mt-[22px] flex">
              <Checkbox
                id="terms"
                className="flex-1"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
              <label htmlFor="terms" className="text-[14px] text-[#5e5e5e]">
                <Trans t={t}>
                  자동 정기 결제에 대한 내용을 포함하여,{' '}
                  <a href={uri['aivatar-term']} target="_blank" rel="noreferrer">
                    서비스 이용약관
                  </a>{' '}
                  및{' '}
                  <a href={uri['aivatar-privacy']} target="_blank" rel="noreferrer">
                    개인정보처리방침
                  </a>
                  을 모두 확인하였으며 동의합니다.
                </Trans>
              </label>
            </div>

            <AivatarButton
              // checkout 이 400 이면 결제창으로 넘어가면 안 된다. 방어가 없으면
              // checkout === undefined 로 분기가 전부 false 가 되어 IMP.request_pay 가
              // 그대로 열린다 (BE aivatar PR #426 FE 계약 1번)
              disabled={
                isUpgrading || isDowngrading || !termsChecked || !checkout || isCheckoutError || blockedByExistingSchedule
              }
              className="btn-main mx-auto mt-[34px] h-[45px] px-[32px] text-[17px]"
              onClick={onClickPayment}
            >
              {isScheduledChange ? t('변경하기') : t('결제하기')}
            </AivatarButton>
          </Grid2>
        </Grid2>
      </div>
    </Section>
  )
}
